import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Chip,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Collapse,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
  Grid,
  LinearProgress,
} from "@mui/material";
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Clear as ClearIcon,
  Visibility as ViewIcon,
  Receipt as ReceiptIcon,
  CalendarMonth as CalendarIcon,
  Category as CategoryIcon,
} from "@mui/icons-material";
import { collection, query, where, getDocs, orderBy } from "firebase/firestore";
import { db, auth } from "../firebase";
import CATEGORY_COLORS from "../constants/categoryColors";
import { EXPENSE_CATEGORIES } from "../constants/expenseCategories";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-IE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount || 0);
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  return date.toLocaleDateString("en-IE", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

// Get contrast text color
function getContrastText(bg) {
  if (!bg) return "#333";
  bg = bg.replace("#", "");
  const r = parseInt(bg.substr(0, 2), 16);
  const g = parseInt(bg.substr(2, 2), 16);
  const b = parseInt(bg.substr(4, 2), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;
  return yiq >= 180 ? "#222" : "#fff";
}

export default function ExpenseBreakdown({ year = new Date().getFullYear() }) {
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [amountRange, setAmountRange] = useState({ min: "", max: "" });
  const [viewMode, setViewMode] = useState("table"); // "table" or "category"

  // Sorting
  const [orderByField, setOrderByField] = useState("date");
  const [orderDirection, setOrderDirection] = useState("desc");

  // Detail dialog
  const [selectedExpense, setSelectedExpense] = useState(null);

  useEffect(() => {
    fetchExpenses();
  }, [year]);

  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) return;

      const q = query(
        collection(db, "receipts"),
        where("uid", "==", user.uid),
        where("receiptYear", "==", year)
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map((doc) => {
        const d = doc.data();
        return {
          id: doc.id,
          ...d,
          amount: parseFloat(d.amount) || 0,
          vat: parseFloat(d.vat) || 0,
          date:
            d.receiptDate ||
            (d.uploadedAt?.toDate
              ? d.uploadedAt.toDate().toISOString().slice(0, 10)
              : null),
          month:
            d.receiptMonth ||
            (d.uploadedAt?.toDate
              ? d.uploadedAt.toDate().getMonth() + 1
              : null),
          vendor: d.fileName || d.transactionDescription || "Unknown",
          category: d.category || "Uncategorized",
          notes: d.notes || "",
          isManual: d.isManual || false,
          autoCreated: d.autoCreated || false,
        };
      });
      setExpenses(data);
    } catch (err) {
      console.error("Error fetching expenses:", err);
    }
    setLoading(false);
  };

  // Filter and sort expenses
  const filteredExpenses = useMemo(() => {
    let result = [...expenses];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (e) =>
          e.vendor.toLowerCase().includes(term) ||
          e.category.toLowerCase().includes(term) ||
          e.notes.toLowerCase().includes(term)
      );
    }

    // Category filter
    if (selectedCategory !== "all") {
      result = result.filter((e) => e.category === selectedCategory);
    }

    // Month filter
    if (selectedMonth !== "all") {
      result = result.filter((e) => e.month === parseInt(selectedMonth));
    }

    // Amount range filter
    if (amountRange.min) {
      result = result.filter((e) => e.amount >= parseFloat(amountRange.min));
    }
    if (amountRange.max) {
      result = result.filter((e) => e.amount <= parseFloat(amountRange.max));
    }

    // Sort
    result.sort((a, b) => {
      let aVal, bVal;
      switch (orderByField) {
        case "amount":
          aVal = a.amount;
          bVal = b.amount;
          break;
        case "category":
          aVal = a.category;
          bVal = b.category;
          break;
        case "vendor":
          aVal = a.vendor.toLowerCase();
          bVal = b.vendor.toLowerCase();
          break;
        default:
          aVal = a.date || "";
          bVal = b.date || "";
      }
      if (orderDirection === "asc") return aVal > bVal ? 1 : -1;
      return aVal < bVal ? 1 : -1;
    });

    return result;
  }, [
    expenses,
    searchTerm,
    selectedCategory,
    selectedMonth,
    amountRange,
    orderByField,
    orderDirection,
  ]);

  // Category breakdown for summary view
  const categoryBreakdown = useMemo(() => {
    const breakdown = {};
    filteredExpenses.forEach((e) => {
      const vatVal = e.vatAmount || e.vat || 0;
      const netAmount = e.amount - vatVal;
      if (!breakdown[e.category]) {
        breakdown[e.category] = { total: 0, count: 0, items: [] };
      }
      breakdown[e.category].total += netAmount;
      breakdown[e.category].count += 1;
      breakdown[e.category].items.push(e);
    });
    return Object.entries(breakdown)
      .map(([category, data]) => ({ category, ...data }))
      .sort((a, b) => b.total - a.total);
  }, [filteredExpenses]);

  // Total filtered amount (Net of VAT)
  const totalFiltered = filteredExpenses.reduce((sum, e) => {
    const vatVal = e.vatAmount || e.vat || 0;
    return sum + (e.amount - vatVal);
  }, 0);

  const handleSort = (field) => {
    if (orderByField === field) {
      setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
    } else {
      setOrderByField(field);
      setOrderDirection("desc");
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedCategory("all");
    setSelectedMonth("all");
    setAmountRange({ min: "", max: "" });
  };

  const hasActiveFilters =
    searchTerm ||
    selectedCategory !== "all" ||
    selectedMonth !== "all" ||
    amountRange.min ||
    amountRange.max;

  if (loading) {
    return (
      <Paper sx={{ p: 3, borderRadius: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading expenses...</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, borderRadius: 3 }}>
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Box>
          <Typography variant="h6" fontWeight={600}>
            💳 Expense Breakdown
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filteredExpenses.length} expenses totaling{" "}
            {formatCurrency(totalFiltered)} (Net)
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(e, v) => v && setViewMode(v)}
            size="small"
          >
            <ToggleButton value="table">
              <Tooltip title="Table View">
                <ReceiptIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="category">
              <Tooltip title="Category View">
                <CategoryIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
          <Button
            variant={showFilters ? "contained" : "outlined"}
            size="small"
            startIcon={<FilterIcon />}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filters{" "}
            {hasActiveFilters &&
              `(${
                [
                  searchTerm,
                  selectedCategory !== "all",
                  selectedMonth !== "all",
                  amountRange.min || amountRange.max,
                ].filter(Boolean).length
              })`}
          </Button>
        </Stack>
      </Stack>

      {/* Filters */}
      <Collapse in={showFilters}>
        <Paper
          variant="outlined"
          sx={{ p: 2, mb: 2, bgcolor: "grey.50", borderRadius: 2 }}
        >
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search vendor, notes..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchTerm("")}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Category</InputLabel>
                <Select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  label="Category"
                >
                  <MenuItem value="all">All Categories</MenuItem>
                  {EXPENSE_CATEGORIES.map((cat) => (
                    <MenuItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Month</InputLabel>
                <Select
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  label="Month"
                >
                  <MenuItem value="all">All Months</MenuItem>
                  {MONTHS.map((m, idx) => (
                    <MenuItem key={idx} value={idx + 1}>
                      {m}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6} md={1.5}>
              <TextField
                fullWidth
                size="small"
                label="Min €"
                type="number"
                value={amountRange.min}
                onChange={(e) =>
                  setAmountRange({ ...amountRange, min: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={6} md={1.5}>
              <TextField
                fullWidth
                size="small"
                label="Max €"
                type="number"
                value={amountRange.max}
                onChange={(e) =>
                  setAmountRange({ ...amountRange, max: e.target.value })
                }
              />
            </Grid>
            <Grid item xs={12} md={2}>
              {hasActiveFilters && (
                <Button
                  fullWidth
                  variant="text"
                  color="secondary"
                  startIcon={<ClearIcon />}
                  onClick={clearFilters}
                >
                  Clear All
                </Button>
              )}
            </Grid>
          </Grid>
        </Paper>
      </Collapse>

      {/* Category View */}
      {viewMode === "category" && (
        <Box>
          {categoryBreakdown.map((cat, idx) => (
            <CategoryRow
              key={cat.category}
              category={cat}
              totalExpenses={totalFiltered}
              onSelectExpense={setSelectedExpense}
            />
          ))}
          {categoryBreakdown.length === 0 && (
            <Typography
              color="text.secondary"
              sx={{ py: 4, textAlign: "center" }}
            >
              No expenses found for the selected filters.
            </Typography>
          )}
        </Box>
      )}

      {/* Table View */}
      {viewMode === "table" && (
        <TableContainer sx={{ maxHeight: 500 }}>
          <Table stickyHeader size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={orderByField === "date"}
                    direction={orderByField === "date" ? orderDirection : "asc"}
                    onClick={() => handleSort("date")}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderByField === "vendor"}
                    direction={
                      orderByField === "vendor" ? orderDirection : "asc"
                    }
                    onClick={() => handleSort("vendor")}
                  >
                    Description
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderByField === "category"}
                    direction={
                      orderByField === "category" ? orderDirection : "asc"
                    }
                    onClick={() => handleSort("category")}
                  >
                    Category
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={orderByField === "amount"}
                    direction={
                      orderByField === "amount" ? orderDirection : "asc"
                    }
                    onClick={() => handleSort("amount")}
                  >
                    Net Cost
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">VAT</TableCell>
                <TableCell align="right">Total</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredExpenses.map((expense) => (
                <TableRow
                  key={expense.id}
                  hover
                  sx={{ cursor: "pointer" }}
                  onClick={() => setSelectedExpense(expense)}
                >
                  <TableCell>
                    <Typography variant="body2">
                      {formatDate(expense.date)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={expense.vendor}>
                      <Typography variant="body2" noWrap sx={{ maxWidth: 250 }}>
                        {expense.vendor}
                      </Typography>
                    </Tooltip>
                    {expense.isManual && (
                      <Chip
                        label="Manual"
                        size="small"
                        color="warning"
                        sx={{ ml: 1, height: 18, fontSize: 10 }}
                      />
                    )}
                    {expense.autoCreated && (
                      <Chip
                        label="Auto"
                        size="small"
                        color="info"
                        sx={{ ml: 1, height: 18, fontSize: 10 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={expense.category}
                      size="small"
                      sx={{
                        backgroundColor:
                          CATEGORY_COLORS[expense.category] || "#eceff1",
                        color: getContrastText(
                          CATEGORY_COLORS[expense.category] || "#eceff1"
                        ),
                        fontWeight: 500,
                        fontSize: "0.7rem",
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={500}>
                      {formatCurrency(expense.amount - expense.vat)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">
                      {formatCurrency(expense.vat)}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" color="text.secondary">
                      {formatCurrency(expense.amount)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedExpense(expense);
                      }}
                    >
                      <ViewIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {filteredExpenses.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No expenses found for the selected filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Expense Detail Dialog */}
      <ExpenseDetailDialog
        expense={selectedExpense}
        onClose={() => setSelectedExpense(null)}
      />
    </Paper>
  );
}

// Category Row Component with expandable items
function CategoryRow({ category, totalExpenses, onSelectExpense }) {
  const [expanded, setExpanded] = useState(false);
  const percentage =
    totalExpenses > 0 ? (category.total / totalExpenses) * 100 : 0;

  return (
    <Paper
      variant="outlined"
      sx={{ mb: 1, borderRadius: 2, overflow: "hidden" }}
    >
      <Box
        sx={{ p: 2, cursor: "pointer", "&:hover": { bgcolor: "grey.50" } }}
        onClick={() => setExpanded(!expanded)}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <Chip
              label={category.category}
              size="small"
              sx={{
                backgroundColor:
                  CATEGORY_COLORS[category.category] || "#eceff1",
                color: getContrastText(
                  CATEGORY_COLORS[category.category] || "#eceff1"
                ),
                fontWeight: 600,
              }}
            />
            <Typography variant="body2" color="text.secondary">
              {category.count} expense{category.count !== 1 ? "s" : ""}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="body1" fontWeight={600}>
              {formatCurrency(category.total)}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ minWidth: 50 }}
            >
              {percentage.toFixed(1)}%
            </Typography>
            <IconButton size="small">
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Stack>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={percentage}
          sx={{ mt: 1, height: 6, borderRadius: 3, bgcolor: "grey.200" }}
        />
      </Box>
      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ p: 1, bgcolor: "grey.50" }}>
          {category.items.map((item) => (
            <Box
              key={item.id}
              sx={{
                p: 1,
                borderRadius: 1,
                "&:hover": { bgcolor: "grey.100" },
                cursor: "pointer",
              }}
              onClick={() => onSelectExpense(item)}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Box>
                  <Typography variant="body2" fontWeight={500}>
                    {item.vendor}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {formatDate(item.date)}
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={500}>
                  {formatCurrency(item.amount)}
                </Typography>
              </Stack>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
}

// Expense Detail Dialog
function ExpenseDetailDialog({ expense, onClose }) {
  if (!expense) return null;

  const categoryInfo = EXPENSE_CATEGORIES.find(
    (c) => c.value === expense.category
  );

  return (
    <Dialog open={!!expense} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h6" fontWeight={600}>
            Expense Details
          </Typography>
          <Chip
            label={expense.category}
            sx={{
              backgroundColor: CATEGORY_COLORS[expense.category] || "#eceff1",
              color: getContrastText(
                CATEGORY_COLORS[expense.category] || "#eceff1"
              ),
              fontWeight: 600,
            }}
          />
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Date
            </Typography>
            <Typography variant="body1" fontWeight={500}>
              {formatDate(expense.date)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Amount
            </Typography>
            <Typography variant="h5" fontWeight={700} color="primary">
              {formatCurrency(expense.amount)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              VAT
            </Typography>
            <Typography variant="body1">
              {formatCurrency(expense.vat)}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="caption" color="text.secondary">
              Net Amount
            </Typography>
            <Typography variant="body1">
              {formatCurrency(expense.amount - expense.vat)}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Divider sx={{ my: 1 }} />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="caption" color="text.secondary">
              Description / Vendor
            </Typography>
            <Typography variant="body1">{expense.vendor}</Typography>
          </Grid>
          {expense.notes && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Notes
              </Typography>
              <Typography variant="body1">{expense.notes}</Typography>
            </Grid>
          )}
          {categoryInfo?.guidance && (
            <Grid item xs={12}>
              <Paper sx={{ p: 2, bgcolor: "info.50", borderRadius: 2 }}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="info.main"
                >
                  Tax Guidance
                </Typography>
                <Typography variant="body2">{categoryInfo.guidance}</Typography>
                <Stack direction="row" spacing={1} mt={1}>
                  <Chip
                    size="small"
                    label={
                      categoryInfo.deductibility === "full"
                        ? "100% Deductible"
                        : categoryInfo.deductibility === "partial"
                        ? "Partially Deductible"
                        : "Conditionally Deductible"
                    }
                    color={
                      categoryInfo.deductibility === "full"
                        ? "success"
                        : categoryInfo.deductibility === "partial"
                        ? "warning"
                        : "info"
                    }
                  />
                  <Chip
                    size="small"
                    label={
                      categoryInfo.vatReclaimable
                        ? "VAT Reclaimable"
                        : "VAT Not Reclaimable"
                    }
                    color={categoryInfo.vatReclaimable ? "success" : "default"}
                    variant="outlined"
                  />
                </Stack>
              </Paper>
            </Grid>
          )}
          <Grid item xs={12}>
            <Stack direction="row" spacing={1} mt={1}>
              {expense.isManual && (
                <Chip label="Manual Entry" size="small" color="warning" />
              )}
              {expense.autoCreated && (
                <Chip
                  label="Auto-created from Bank"
                  size="small"
                  color="info"
                />
              )}
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
